import { useState } from 'react';
import { Chip, ProviderBadge } from '@jotpsych/ui';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LaunchIcon from '@mui/icons-material/Launch';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import {
  Box,
  Button,
  Collapse,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { episodeStatusColor, episodeStatusLabel } from '../labels';
import {
  composeSig,
  formatRefills,
  historyIsPrescriptions,
  isInactiveEpisode,
  prescriptionHistory,
  resolveDateCell,
  resolvePrescriber,
  splitPharmacy,
} from '../vm/formatters';
import type { MedicationEpisodeVM } from '../vm/types';

import { PastPrescriptionRow } from './PastPrescriptionRow';
import { RowActionsMenu } from './RowActionsMenu';

const EM_DASH = '—';
const COLUMN_COUNT = 7;

export interface MedicationRowActions {
  /** Opens the medication edit modal (charted/self-reported rows only). */
  onEdit?: (episodeId: string) => void;
  /**
   * Opens DoseSpot for this patient in a new tab — offered instead of Edit on
   * rows backed by a DoseSpot prescription, which is the legal order of
   * record: local edits would diverge from it and be overwritten by the next
   * reconcile.
   */
  onManageInDoseSpot?: (episodeId: string) => void;
  /** Soft discontinue (PATCH is_active=false) — offered on active/held rows. */
  onDiscontinue?: (episodeId: string) => void;
  /** Soft DELETE (row survives as inactive_reason='removed') — offered on already-inactive rows only. */
  onRemove?: (episodeId: string) => void;
  /** Notification when the past-prescriptions drawer is toggled. */
  onHistoryToggled?: (episodeId: string, expanded: boolean) => void;
}

interface MedicationRowProps extends MedicationRowActions {
  episode: MedicationEpisodeVM;
  defaultExpanded?: boolean;
}

/**
 * One medication in the AMD-style list: the current prescription across the
 * familiar columns, plus a collapsible drawer of its past prescriptions.
 */
export const MedicationRow: React.FC<MedicationRowProps> = ({
  episode,
  defaultExpanded = false,
  onEdit,
  onManageInDoseSpot,
  onDiscontinue,
  onRemove,
  onHistoryToggled,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const regimen = episode.actualUse ?? episode.prescribed;
  const order = episode.orders[0];
  const pharmacy = order?.pharmacy ? splitPharmacy(order.pharmacy) : null;
  const history = prescriptionHistory(episode);
  // Chart-only histories (legacy/self-reported meds) are dose-change trails,
  // not prescriptions — the drawer vocabulary follows the content.
  const historyNoun = historyIsPrescriptions(history) ? 'past prescriptions' : 'history';
  const historyHeader = historyIsPrescriptions(history) ? 'Prescription history' : 'History';
  const prescriber = resolvePrescriber(episode);
  const dateCell = resolveDateCell(episode);
  const inactive = isInactiveEpisode(episode);
  const hasSource = (source: string) =>
    episode.sources.some((provenance) => provenance.source === source);
  const isDoseSpotRx = hasSource('dosespot_order');
  // A self-reported med that ORIGINATED in DoseSpot (no jotpsych/chart
  // provenance) has no outbound sync identity: a chart edit would push a
  // duplicate SRM into DoseSpot and a discontinue would silently no-op there.
  // Chart-owned meds (clinician_manual present) round-trip correctly even
  // when DoseSpot echo events also appear on the row.
  const isDoseSpotOwnedSelfReport =
    !isDoseSpotRx && hasSource('patient_report') && !hasSource('clinician_manual');
  const managedInDoseSpot = isDoseSpotRx || isDoseSpotOwnedSelfReport;
  const cellSx = { verticalAlign: 'top', py: 1.5 } as const;

  return (
    <>
      <TableRow sx={{ '& > td': { borderBottom: expanded ? 'none' : undefined } }}>
        <TableCell sx={cellSx}>
          <Stack spacing={0.25}>
            <Tooltip title={episode.fullName} placement="top-start">
              <Typography variant="body2" sx={{ fontWeight: 600, width: 'fit-content' }}>
                {episode.drugName}
              </Typography>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {composeSig(regimen)}
            </Typography>
            {episode.target && (
              <Typography variant="caption" color="text.disabled">
                {episode.target}
              </Typography>
            )}
            {history.length > 0 && (
              <Button
                variant="text"
                size="small"
                disableRipple
                onClick={() => {
                  const next = !expanded;
                  setExpanded(next);
                  onHistoryToggled?.(episode.id, next);
                }}
                endIcon={
                  <KeyboardArrowDownIcon
                    sx={{
                      transition: 'transform 150ms ease',
                      transform: expanded ? 'rotate(180deg)' : 'none',
                    }}
                  />
                }
                sx={{
                  alignSelf: 'flex-start',
                  mt: 0.5,
                  px: 0.5,
                  ml: -0.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                {expanded ? `Hide ${historyNoun}` : `View ${historyNoun} (${history.length})`}
              </Button>
            )}
          </Stack>
        </TableCell>

        <TableCell align="right" sx={cellSx}>
          <Typography variant="body2">{formatRefills(order)}</Typography>
        </TableCell>

        <TableCell sx={cellSx}>
          {pharmacy ? (
            <>
              <Typography variant="body2">{pharmacy.name}</Typography>
              {pharmacy.store && (
                <Typography variant="caption" color="text.secondary">
                  {pharmacy.store}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {EM_DASH}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={cellSx}>
          {prescriber ? (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ProviderBadge name={prescriber} size="regular" />
              <Typography variant="body2">{prescriber}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {EM_DASH}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={cellSx}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {dateCell.value}
          </Typography>
          {dateCell.caption && (
            <Typography variant="caption" color="text.secondary">
              {dateCell.caption}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={cellSx}>
          <Chip
            variant="soft"
            size="xSmall"
            color={episodeStatusColor[episode.episodeStatus]}
            label={episodeStatusLabel[episode.episodeStatus]}
          />
        </TableCell>

        {/* Row actions. A DoseSpot prescription is the legal order of record:
            it can only be managed in DoseSpot (discontinuing or editing it from
            the chart would diverge from the pharmacy and, worse, mint a
            separate spine row rather than stop the real Rx). So those rows offer
            ONLY "Manage in DoseSpot". Self-reported / manual meds get Edit plus
            Discontinue (soft stop → Inactive) or Remove (soft DELETE; the row
            survives server-side as inactive_reason='removed'). Callbacks come
            from the host container; without them the items are inert
            (design-review stories). */}
        <TableCell align="right" sx={cellSx}>
          <RowActionsMenu
            ariaLabel={`Actions for ${episode.drugName}`}
            onSelect={(key) => {
              if (key === 'edit') onEdit?.(episode.id);
              if (key === 'manage') onManageInDoseSpot?.(episode.id);
              if (key === 'remove') onRemove?.(episode.id);
              if (key === 'discontinue') onDiscontinue?.(episode.id);
            }}
            actions={
              episode.isSuggested
                ? [
                    // An AI proposal: Review opens the edit modal prefilled
                    // (saving accepts-with-edits via PATCH on the action_id);
                    // Dismiss rejects it (DELETE on the action_id).
                    {
                      key: 'edit',
                      label: 'Review & accept',
                      icon: <EditOutlinedIcon fontSize="small" />,
                    },
                    {
                      key: 'remove',
                      label: 'Dismiss',
                      icon: (
                        <DeleteOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
                      ),
                      destructive: true,
                    },
                  ]
                : managedInDoseSpot
                  ? [
                      {
                        key: 'manage',
                        label: 'Manage in DoseSpot',
                        icon: <LaunchIcon fontSize="small" />,
                      },
                    ]
                  : [
                      { key: 'edit', label: 'Edit', icon: <EditOutlinedIcon fontSize="small" /> },
                      inactive
                        ? {
                            key: 'remove',
                            label: 'Remove',
                            icon: (
                              <DeleteOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
                            ),
                            destructive: true,
                          }
                        : {
                            key: 'discontinue',
                            label: 'Discontinue',
                            icon: <StopCircleOutlinedIcon fontSize="small" />,
                          },
                    ]
            }
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: 'backgroundColors.tertiary',
                borderRadius: 1,
                px: 2,
                py: 1.5,
                my: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  color: 'text.secondary',
                }}
              >
                {historyHeader}
              </Typography>

              {episode.trial && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, fontStyle: 'italic' }}
                >
                  {[
                    episode.trial.maxDose ? `Max ${episode.trial.maxDose}` : null,
                    episode.trial.duration,
                    episode.trial.reasonStopped,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}

              <Box sx={{ mt: 1 }}>
                {history.map((event) => (
                  <PastPrescriptionRow key={event.id} event={event} />
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
